import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { IMPORT_COLUMNS, formatNumber, messages, type ImportAnalyzeResult } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';

/**
 * CSV 取込（F05.4）。
 * ファイル選択 → 列マッピング → 検証結果の確認 → 実行 の 3 ステップ。
 */
export const CsvImportDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<'select' | 'map'>('select');
  const [analysis, setAnalysis] = useState<ImportAnalyzeResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [dedupeStrategy, setDedupeStrategy] = useState<'domain' | 'phone' | 'none'>('domain');

  const analyze = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.upload<SingleResponse<ImportAnalyzeResult>>('/imports/companies/analyze', formData);
    },
    onSuccess: (result) => {
      setAnalysis(result.data);
      setMapping(result.data.suggestedMapping);
      setStep('map');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const execute = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ importId: string }>>('/imports/companies/execute', {
        importId: analysis?.importId,
        mapping,
        dedupeStrategy,
      }),
    onSuccess: () => {
      toast.success('取込を開始しました', { description: '完了すると通知でお知らせします' });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      close();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const close = () => {
    onOpenChange(false);
    setStep('select');
    setAnalysis(null);
    setMapping({});
  };

  const hasNameColumn = Object.values(mapping).includes('name');

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="lg:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{messages.common.import}</DialogTitle>
          <DialogDescription>
            {step === 'select'
              ? 'CSV ファイルを選択してください（UTF-8、先頭行が列名）'
              : '列の対応を確認して取り込みます'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {step === 'select' && (
            <Field label="CSV ファイル" required>
              {(props) => (
                <Input
                  {...props}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) analyze.mutate(file);
                  }}
                  disabled={analyze.isPending}
                />
              )}
            </Field>
          )}

          {step === 'map' && analysis && (
            <>
              <div className="rounded-card border border-border bg-surface p-3 text-body">
                <p className="font-semibold">{analysis.fileName}</p>
                <p className="tabular mt-1 text-note text-foreground-muted">
                  全 {formatNumber(analysis.totalRows)} 行／取り込み可能{' '}
                  {formatNumber(analysis.validation.importable)} 件・重複{' '}
                  {formatNumber(analysis.validation.duplicate)} 件・エラー{' '}
                  {formatNumber(analysis.validation.error)} 件
                </p>
              </div>

              {analysis.validation.errors.length > 0 && (
                <details className="rounded-card border border-border p-3">
                  <summary className="cursor-pointer text-body font-semibold">
                    エラーの内容（{analysis.validation.errors.length} 件）
                  </summary>
                  <ul className="mt-2 space-y-1 text-note text-foreground-muted">
                    {analysis.validation.errors.slice(0, 20).map((error) => (
                      <li key={error.row}>
                        <span className="tabular">{error.row} 行目</span>：{error.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="space-y-2">
                <p className="text-body font-semibold">列の対応</p>
                <div className="max-h-64 space-y-2 overflow-y-auto rounded-card border border-border p-3">
                  {analysis.headers.map((header) => (
                    <div key={header} className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-body">{header}</span>
                      <span aria-hidden className="text-foreground-subtle">
                        →
                      </span>
                      <Select
                        value={mapping[header] ?? ''}
                        onChange={(e) =>
                          setMapping((prev) => ({ ...prev, [header]: e.target.value }))
                        }
                        aria-label={`${header} の対応先`}
                        className="h-10 w-44 text-body"
                      >
                        <option value="">取り込まない</option>
                        {IMPORT_COLUMNS.map((column) => (
                          <option key={column.key} value={column.key}>
                            {column.label}
                            {column.required ? '（必須）' : ''}
                          </option>
                        ))}
                      </Select>
                    </div>
                  ))}
                </div>
                {!hasNameColumn && (
                  <p className="text-note font-semibold text-foreground">
                    企業名に対応する列を選んでください
                  </p>
                )}
              </div>

              <Field label="重複判定の方法" required>
                {(props) => (
                  <Select
                    {...props}
                    value={dedupeStrategy}
                    onChange={(e) => setDedupeStrategy(e.target.value as typeof dedupeStrategy)}
                  >
                    <option value="domain">ドメイン一致で既存を除く</option>
                    <option value="phone">電話番号一致で既存を除く</option>
                    <option value="none">すべて新規として追加する</option>
                  </Select>
                )}
              </Field>

              <details className="rounded-card border border-border p-3">
                <summary className="cursor-pointer text-body font-semibold">
                  先頭 {analysis.preview.length} 行のプレビュー
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-note">
                    <thead>
                      <tr className="border-b border-border">
                        {analysis.headers.map((header) => (
                          <th key={header} scope="col" className="px-2 py-1 text-left font-semibold">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.preview.slice(0, 5).map((row, index) => (
                        <tr key={index} className="border-b border-border last:border-0">
                          {analysis.headers.map((header) => (
                            <td key={header} className="max-w-40 truncate px-2 py-1">
                              {row[header]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={close}>
            {messages.common.cancel}
          </Button>
          {step === 'map' && (
            <Button
              size="lg"
              onClick={() => execute.mutate()}
              disabled={execute.isPending || !hasNameColumn}
              disabledReason={!hasNameColumn ? '企業名の列を選んでください' : undefined}
            >
              取り込む
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
